import type { PropertyValidationResult } from '../../../types/properties';

export default function validator(properties: Record<string, any>): PropertyValidationResult {
  const errors: Array<{ key: string; message: string }> = [];
  const warnings: Array<{ key: string; message: string }> = [];

  // Validate model provider
  if (!properties.provider) {
    errors.push({ key: 'provider', message: 'Model provider is required' });
  }

  // Validate model selection
  if (!properties.model) {
    errors.push({ key: 'model', message: 'Model is required' });
  }

  // Validate credentials
  if (!properties.credentialId) {
    errors.push({ key: 'credentialId', message: 'Credentials are required' });
  }

  // Validate prompts
  if (!properties.userPrompt || properties.userPrompt.trim() === '') {
    errors.push({ key: 'userPrompt', message: 'User message is required' });
  }

  // Validate system prompt length
  if (properties.systemPrompt && properties.systemPrompt.length > 10000) {
    warnings.push({ key: 'systemPrompt', message: 'System prompt is very long and may affect performance' });
  }

  // Validate model parameters
  if (properties.maxTokens !== undefined) {
    if (properties.maxTokens < 1) {
      errors.push({ key: 'maxTokens', message: 'Max tokens must be at least 1' });
    }
    if (properties.maxTokens > 200000) {
      errors.push({ key: 'maxTokens', message: 'Max tokens cannot exceed 200,000' });
    }
  }

  if (properties.temperature !== undefined) {
    if (properties.temperature < 0 || properties.temperature > 2) {
      errors.push({ key: 'temperature', message: 'Temperature must be between 0 and 2' });
    }
  }

  if (properties.maxRetries !== undefined) {
    if (properties.maxRetries < 1 || properties.maxRetries > 10) {
      errors.push({ key: 'maxRetries', message: 'Max retries must be between 1 and 10' });
    }
  }

  if (properties.maxHistoryMessages !== undefined) {
    if (properties.maxHistoryMessages < 1 || properties.maxHistoryMessages > 100) {
      errors.push({ key: 'maxHistoryMessages', message: 'Max history messages must be between 1 and 100' });
    }
  }

  // Validate tools
  if (properties.enableTools && properties.availableTools) {
    const toolsList = Array.isArray(properties.availableTools) ? properties.availableTools : [];
    
    if (toolsList.length === 0) {
      warnings.push({ key: 'availableTools', message: 'Tools are enabled but no tools are configured' });
    }

    toolsList.forEach((tool: any, index: number) => {
      if (!tool.name || tool.name.trim() === '') {
        errors.push({ key: `availableTools[${index}].name`, message: 'Tool name is required' });
      }

      if (!tool.description || tool.description.trim() === '') {
        errors.push({ key: `availableTools[${index}].description`, message: 'Tool description is required' });
      }

      // Validate tool name format (should be snake_case)
      if (tool.name && !/^[a-z][a-z0-9_]*$/.test(tool.name)) {
        warnings.push({ 
          key: `availableTools[${index}].name`, 
          message: 'Tool name should be in snake_case format (e.g., search_database)' 
        });
      }
    });
  }

  // Validate conversation history configuration
  if (properties.includeConversationHistory) {
    if (!properties.conversationHistoryKey || properties.conversationHistoryKey.trim() === '') {
      errors.push({ key: 'conversationHistoryKey', message: 'History key is required when conversation history is enabled' });
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
}
